import React, { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { PropertyValue, Vec3, Camera2DPos, Camera3DPos } from './parser/propTypes';
import { ChromePicker, ColorResult } from 'react-color';
import { colorPickerStyles } from './styles/colorPickerStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';

// Components that allow editing of shader properties
export interface PropComponentProps {
  // Display name of the property
  name: string;
  // Current value of the property
  value: PropertyValue;
  // If the property should allow editing
  enabled: boolean;
  // Callback to set the property
  setValue: (val: PropertyValue) => void;
  // If the property is a selected keyframe
  activeKeyframe: boolean;
  // Callback to toggle keyframe status
  toggleKeyframe: () => void;
  // Arguments from the $prop declaration
  args: any;
  // If the property has a inherent const/uniform status and can't be toggled
  lockedConst: boolean;
  // If the property should display animation/keyframe controls
  doAnimate: boolean;
}

// A forceConst toggle (uniform/const lock)
function ForceConstToggle(props: PropComponentProps) {
  if(props.lockedConst) {
    return (
      <>
        <div className="constUniformToggle lockedConst">
        <FontAwesomeIcon icon={faLock} />
        </div>
        {props.doAnimate && 
          <div className={`keyframeButton diamond nonKeyframable ${props.activeKeyframe ? "keyframeSelected" : ""}`} />
        }
      </>
    );
  }

  const keyframed = !props.enabled || props.activeKeyframe;

  // callback to toggle forceConst
  const toggle = () => {
    if(!keyframed) {
      props.setValue({ value: props.value.value, forceConst: !props.value.forceConst });
    }
  };

  const keyframable = keyframed || !props.value.forceConst;

  const toggleKeyframe = () => {
    if(keyframable) {
      props.toggleKeyframe();
    }
  };

  return (
    <>
      <div onClick={toggle} className={`constUniformToggle ${keyframed ? "lockedConst" : ""}`}>
        {props.value.forceConst &&
          <FontAwesomeIcon icon={faLock} />
        }
        {!props.value.forceConst &&
          <FontAwesomeIcon icon={faLockOpen} />
        }
      </div>
      {props.doAnimate &&
        <div 
          className={`keyframeButton diamond ${props.activeKeyframe ? "keyframeSelected" : ""} ${!keyframable ? "nonKeyframable" : ""}`}
          onClick={toggleKeyframe}
        />
      }
    </>
  );
}

// A property's display name followed by children next to a forceConst toggle
export function PropLayout(props: PropComponentProps & { children: ReactNode } ) {
  return (
    <>
      <div className={`propName ${!props.enabled ? "propDisabled" : ""}`}>{props.name}</div>
      <div className={`propValueContainer ${!props.enabled ? "propDisabled" : ""}`}>
        <div className="propValue">{props.children}</div>
        <ForceConstToggle {...props}/>
      </div>
    </>
  );
}

interface SliderInputProps {
  low: number;
  high: number;
  step: number;
  value: number;
  setValue: (value: number) => void;
  label?: string;
  labelWidth?: string;
}

// A combined slider + number input box
function SliderInput(props: SliderInputProps) {
  const { low, high, step, value, setValue, label, labelWidth } = props;

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(+event.target.value);
  };

  return (
    <div className="flex">
      {label !== undefined &&
        <div className="subpropLabel" style={{width: labelWidth}}>{label}</div>
      }
      <div className="subpropFlexExpand">
        <input 
          type="range" step={step} min={low} max={high} 
          value={value}
          onChange={onChange}>
        </input>
      </div>
      <div className="subpropNumberContainer">
        <input 
          type="number" step={step} min={low} max={high}
          value={value} 
          onChange={onChange}
        />
      </div>
    </div>
  );
}

interface NumberInputProps {
  value: number;
  setValue: (value: number) => void;
  label?: string;
  labelWidth?: string;
}

// A number input box
function NumberInput(props: NumberInputProps) {
  const {value, setValue, label, labelWidth} = props;
  const [strInput, setStrInput] = useState("");
  useEffect(() => setStrInput(value.toString()), [value, setStrInput]);

  return (
    <div className="flex">
      {label !== undefined &&
        <div className="subpropLabel" style={{width: labelWidth}}>{label}</div>
      }
      <div className="subpropFlexExpand">
        <form onSubmit={(event) => {
          setValue(+strInput);
          event.preventDefault();
        }}>
          <input type="text"
            value={strInput}
            onChange={(event) => {
              setStrInput(event.target.value);
            }}
            onBlur={(event) => {
              setValue(+event.target.value);
            }}
          />
        </form>
      </div>
    </div>
  );
}

interface VectorInputProps {
  value: number[];
  setValue: (value: number[]) => void;
  isSlider: boolean;
  low?: number;
  high?: number;
  step?: number;
  labels?: string[];
  labelWidth: string;
}

// A slider/number input box for a vector
export function VectorInput(props: VectorInputProps) {
  const {value, setValue, isSlider} = props;
  // if the type is a vector, we need a label for each vector component
  const compLabel = props.labels !== undefined ? props.labels : ["X", "Y", "Z", "W"];
  const isVector = value.length > 1;

  return (
    <div className={`${isVector ? "subpropIndent" : ""}`}>
      {value.map((comp, compIndex) => {
        const label = isVector ? compLabel[compIndex] : undefined;
        const setComp = (newComp: number) => 
          setValue([...value.slice(0, compIndex), newComp, ...value.slice(compIndex + 1)]);
        const commonProps = { key: compIndex, label: label, labelWidth: props.labelWidth, value: comp, setValue: setComp};
        return isSlider ?
          <SliderInput {...commonProps} low={props.low!} high={props.high!} step={props.step!}/> :
          <NumberInput {...commonProps} />;
      })}
      </div>
  )
}

// $prop slider
// $prop input
export function NumberSlider(props: PropComponentProps) {
  const value = props.value.value as number[];
  return (
    <PropLayout {...props}>
      <VectorInput
        value={value}
        setValue={(newVal: number[]) => props.setValue({...props.value, value: newVal})}
        isSlider={props.args.length >= 2}
        low={props.args[0]}
        high={props.args[1]}
        step={props.args[2]}
        labelWidth="1rem"
      />
    </PropLayout>
  );
}

// convert between Vec3 and html color
export function vec3ToHTMLColor(color: Vec3): string {
  return `rgba(${color[0] * 255}, ${color[1] * 255}, ${color[2] * 255}, 1.0)`;
}

// convert an react-color color to vec3
export function colorResultToVec3(color: ColorResult): Vec3 {
  return [color.rgb.r / 255.0, color.rgb.g / 255.0, color.rgb.b / 255.0];
}

// $prop color
export function ColorPicker(props: PropComponentProps) {
  const color = props.value.value as Vec3;
  const htmlColor = vec3ToHTMLColor(color);

  // If the color picker is toggled
  const [enabled, setEnabled] = useState(false);

  const toggleEnabled = () => {
    setEnabled((enabled) => !enabled);
  };

  const closeEnabled = () => {
    setEnabled(false);
  };

  return (
    <PropLayout {...props}>
      <div 
        className="colorDiv" 
        style={{background: htmlColor}}
        onClick={toggleEnabled}
      />
      {enabled &&
        <div className="flex colorPickerContainer">
        <ChromePicker
            color={htmlColor}
            onChange={(color) => {
              props.setValue({ value: colorResultToVec3(color), forceConst: props.value.forceConst });
            }}
            styles={colorPickerStyles}
          />
        <div
          className="gradientColorPickerClose"
          onClick={closeEnabled}>X</div>
      </div>
      }
    </PropLayout>
  );
}

interface CheckboxInputProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}

export function CheckboxInput(props: CheckboxInputProps) {
  return (
    <label className="checkboxLabel">
          <input
            type="checkbox" 
            checked={props.checked}
            onChange={(event) => props.onChange(event.target.checked)}
          />
          <span className="checkmark"/>
          {props.label}
        </label>
  );
}

// $prop camera_2d
export function Camera2D(props: PropComponentProps) {
  const val = props.value.value as Camera2DPos;
  return (
    <PropLayout {...props}>
      <div className="subpropIndent">
        <SliderInput
          label={"Speed"} labelWidth={"3rem"}
          low={0.0} high={3.0} step={0.001} 
          value={val.speed}
          setValue={(speed) => {
            props.setValue({ ...props.value, value: { ...val, speed: speed } });
          }}/>
      </div>
      <VectorInput
        value={[...val.center, val.height]}
        setValue={(newVal: number[]) =>
          props.setValue({...props.value, value: {...val, center: [newVal[0], newVal[1]], height: newVal[2] }})
        }
        isSlider={false}
        labelWidth="3rem"
        labels={["X", "Y", "Height"]}
      />
    </PropLayout>
  );
}

// $prop camera_3d
export function Camera3D(props: PropComponentProps) {
  const val = props.value.value as Camera3DPos;
  return (
    <PropLayout {...props}>
      <div className="subpropIndent">
        <SliderInput
          label={"Speed"} labelWidth={"3rem"}
          low={0.0} high={3.0} step={0.001} 
          value={val.speed}
          setValue={(speed) => {
            props.setValue({ ...props.value, value: { ...val, speed: speed } });
          }}
        />
        <CheckboxInput
          checked={val.dynamic}
          onChange={(checked) => {
            props.setValue({...props.value, value: { ...val, dynamic: checked }})
          }}
          label="Distance Based Speed (Progressive Rendering Only)"
        />
      </div>
      <VectorInput
        value={[...val.pos, ...val.direction, ...val.vup]}
        setValue={(newVal: number[]) => props.setValue({...props.value, value: {
          ...val, pos: newVal.slice(0, 3), direction: newVal.slice(3, 6), vup: newVal.slice(6, 9)
        }})}
        isSlider={false}
        labelWidth="3rem"
        labels={["Pos X", "Pos Y", "Pos Z", "Dir X", "Dir Y", "Dir Z", "Vup X", "Vup Y", "Vup Z"]}
      />
    </PropLayout>
  );
}

// $prop checkbox
export function Checkbox(props: PropComponentProps) {
  const value = props.value.value as boolean;

  return (
    <div className={`propValueContainer ${!props.enabled ? "propDisabled" : ""}`}>
      <div className="propValue">
        <CheckboxInput 
          checked={value} 
          onChange={(checked) => props.setValue({ ...props.value, value: checked })}
          label={props.name}
        />
      </div>
      <ForceConstToggle {...props}/>
    </div>
  );
}